import React, { useState, useEffect } from 'react';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    Button,
    VStack,
    HStack,
    Text,
    Badge,
    useToast,
    Input,
    Select,
    IconButton,
    Tooltip,
    Spinner,
    Alert,
    AlertIcon,
    Flex,
    useColorModeValue,
    Card,
    CardBody,
    Avatar,
    SimpleGrid,
    InputGroup,
    InputLeftElement,
    TableContainer,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Center,
} from '@chakra-ui/react';
import {
    Search,
    Calendar,
    Users,
    Filter,
    RefreshCw,
    Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    TrainerWithClientsData,
    TrainerClientData
} from '../../../../services/agencyRelationshipService';
import CustomPagination from '../../../../components/pagination/Pagination';

interface ViewTrainerClientsModalProps {
    isOpen: boolean;
    onClose: () => void;
    trainer: TrainerWithClientsData | null;
}

const ViewTrainerClientsModal: React.FC<ViewTrainerClientsModalProps> = ({
    isOpen,
    onClose,
    trainer
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [filteredClients, setFilteredClients] = useState<TrainerClientData[]>([]);
    const [paginatedClients, setPaginatedClients] = useState<TrainerClientData[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(5); // Show 5 clients per page

    const toast = useToast();
    const navigate = useNavigate();

    const cardBg = useColorModeValue('white', 'navy.700');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const bgColor = useColorModeValue('gray.50', 'navy.800');
    const textColor = useColorModeValue('navy.700', 'white');
    const textColorSecondary = useColorModeValue('gray.600', 'gray.400');
    const modalBg = useColorModeValue('white', 'navy.700');

    // Filter clients based on search term and status
    useEffect(() => {
        if (!trainer) {
            setFilteredClients([]);
            setPaginatedClients([]);
            return;
        }

        let clients = trainer.clientRelations;

        // Filter by status
        if (statusFilter === 'active') {
            clients = clients.filter(client => client.isActive);
        } else if (statusFilter === 'inactive') {
            clients = clients.filter(client => !client.isActive);
        }

        // Filter by search term
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            clients = clients.filter(client =>
                client.client.name.toLowerCase().includes(searchLower) ||
                client.client.email.toLowerCase().includes(searchLower) ||
                client.client.phone.includes(searchTerm)
            );
        }

        setFilteredClients(clients);
        
        // Reset to first page when filters change
        setCurrentPage(1);
    }, [trainer, searchTerm, statusFilter]);

    // Handle pagination
    useEffect(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        setPaginatedClients(filteredClients.slice(startIndex, endIndex));
    }, [filteredClients, currentPage, pageSize]);

    const handleViewClientCalendar = (clientId: string, clientName: string) => {
        if (!trainer) return;

        // Navigate to the calendar page with client information
        navigate(`/agency/calendar/${clientId}?client_name=${encodeURIComponent(clientName)}&trainer_name=${encodeURIComponent(trainer.trainer.name)}&trainer_id=${trainer.trainer.id}`);
        onClose(); // Close modal after navigation
    };

    const handleClearFilters = () => {
        setSearchTerm('');
        setStatusFilter('all');
        setCurrentPage(1);
    };

    if (!trainer) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="6xl"
            scrollBehavior="inside"
        >
            <ModalOverlay />
            <ModalContent maxW="90vw" maxH="90vh" bg={modalBg}>
                <ModalHeader>
                    <VStack align="start" spacing={2}>
                        <HStack spacing={3}>
                            <Avatar
                                name={trainer.trainer.name}
                                size="md"
                                bg="blue.500"
                                color="white"
                            />
                            <VStack align="start" spacing={0}>
                                <Text fontSize="xl" fontWeight="bold" color={textColor}>
                                    {trainer.trainer.name}'s Clients
                                </Text>
                                <Text fontSize="md" color={textColorSecondary}>
                                    {trainer.trainer.specialization} • {filteredClients.length} of {trainer.clientRelations.length} clients
                                </Text>
                            </VStack>
                        </HStack>
                    </VStack>
                </ModalHeader>
                <ModalCloseButton color={textColor} />

                <ModalBody bg={bgColor}>
                    <VStack spacing={6} align="stretch">
                        {/* Filters */}
                        <Card bg={cardBg} borderColor={borderColor}>
                            <CardBody>
                                <VStack spacing={4}>
                                    <Flex justify="space-between" align="center" w="full">
                                        <HStack spacing={3}>
                                            <Text fontWeight="semibold" fontSize="md" color={textColor}>
                                                <Filter size={16} style={{ display: 'inline', marginRight: '8px' }} />
                                                Filters
                                            </Text>
                                        </HStack>
                                        <HStack spacing={2}>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                leftIcon={<RefreshCw size={14} />}
                                                onClick={handleClearFilters}
                                            >
                                                Clear
                                            </Button>
                                        </HStack>
                                    </Flex>

                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                                        <InputGroup>
                                            <InputLeftElement pointerEvents="none">
                                                <Search size={16} color="gray" />
                                            </InputLeftElement>
                                            <Input
                                                placeholder="Search by name, email, or phone..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                bg={useColorModeValue('white', 'navy.700')}
                                                borderColor={borderColor}
                                                color={textColor}
                                                _focus={{ 
                                                    borderColor: 'blue.500',
                                                    boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                                }}
                                                _placeholder={{ 
                                                    color: textColorSecondary
                                                }}
                                            />
                                        </InputGroup>

                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            bg={useColorModeValue('white', 'navy.700')}
                                            borderColor={borderColor}
                                            color={textColor}
                                            _focus={{ 
                                                borderColor: 'blue.500',
                                                boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)'
                                            }}
                                        >
                                            <option value="all">All Status</option>
                                            <option value="active">Active Only</option>
                                            <option value="inactive">Inactive Only</option>
                                        </Select>
                                    </SimpleGrid>
                                </VStack>
                            </CardBody>
                        </Card>

                        {/* Content */}
                        {isLoading ? (
                            <Center py={8}>
                                <Spinner size="xl" />
                            </Center>
                        ) : filteredClients.length === 0 ? (
                            <Alert status="info">
                                <AlertIcon />
                                {searchTerm || statusFilter !== 'all'
                                    ? 'No clients found matching your criteria.'
                                    : 'This trainer has no clients assigned.'
                                }
                            </Alert>
                        ) : (
                            <TableContainer bg={cardBg} borderRadius="xl" border="1px" borderColor={borderColor}>
                                <Table variant="simple" size="md">
                                    <Thead bg={useColorModeValue('gray.50', 'navy.800')}>
                                        <Tr>
                                            <Th 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Client
                                            </Th>
                                            <Th 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Contact
                                            </Th>
                                            <Th 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Start Date
                                            </Th>
                                            <Th 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Status
                                            </Th>
                                            <Th 
                                                borderColor={borderColor}
                                                fontSize={{ sm: '10px', lg: '12px' }}
                                                color="gray.400"
                                            >
                                                Actions
                                            </Th>
                                        </Tr>
                                    </Thead>
                                    <Tbody>
                                        {paginatedClients.map((client) => (
                                            <Tr 
                                                key={client.relationshipId}
                                                _hover={{ bg: useColorModeValue('gray.50', 'whiteAlpha.100') }}
                                            >
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    borderColor="transparent"
                                                >
                                                    <HStack spacing={3}>
                                                        <Avatar name={client.client.name} size="sm" />
                                                        <VStack align="start" spacing={0}>
                                                            <Text fontWeight="medium" color={textColor}>
                                                                {client.client.name}
                                                            </Text>
                                                            <Text fontSize="xs" color={textColorSecondary}>
                                                                {client.client.gender || 'N/A'}
                                                            </Text>
                                                        </VStack>
                                                    </HStack>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    borderColor="transparent"
                                                >
                                                    <VStack align="start" spacing={0}>
                                                        <Text fontSize="sm" color={textColor}>
                                                            {client.client.email}
                                                        </Text>
                                                        <Text fontSize="xs" color={textColorSecondary}>
                                                            {client.client.phone}
                                                        </Text>
                                                    </VStack>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    borderColor="transparent"
                                                >
                                                    <Text fontSize="sm" color={textColor}>
                                                        {new Date(client.startDate).toLocaleDateString()}
                                                    </Text>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    borderColor="transparent"
                                                >
                                                    <Badge
                                                        colorScheme={client.isActive ? 'green' : 'red'}
                                                        variant="subtle"
                                                        borderRadius="full"
                                                        px={3}
                                                        py={1}
                                                    >
                                                        {client.isActive ? 'Active' : 'Inactive'}
                                                    </Badge>
                                                </Td>
                                                <Td
                                                    fontSize={{ sm: '14px' }}
                                                    borderColor="transparent"
                                                >
                                                    <HStack spacing={2}>
                                                        <Tooltip label="View Exercise Calendar">
                                                            <IconButton
                                                                aria-label="View calendar"
                                                                icon={<Calendar size={16} />}
                                                                size="sm"
                                                                colorScheme="blue"
                                                                variant="solid"
                                                                onClick={() => handleViewClientCalendar(client.client.id, client.client.name)}
                                                            />
                                                        </Tooltip>
                                                    </HStack>
                                                </Td>
                                            </Tr>
                                        ))}
                                    </Tbody>
                                </Table>
                            </TableContainer>
                        )}

                        {/* Pagination */}
                        {filteredClients.length > pageSize && (
                            <CustomPagination 
                                totalResults={filteredClients.length} 
                                pageSize={pageSize} 
                                onPageChange={(newPage: number) => {
                                    setCurrentPage(newPage + 1); // CustomPagination uses 0-based indexing
                                }}
                            />
                        )}
                    </VStack>
                </ModalBody>

                <ModalFooter>
                    <HStack spacing={3}>
                        <Text fontSize="sm" color={textColorSecondary}>
                            {filteredClients.length > pageSize ? (
                                <>
                                    Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, filteredClients.length)} of {filteredClients.length} clients
                                    {filteredClients.length !== trainer.clientRelations.length && (
                                        <> (filtered from {trainer.clientRelations.length} total)</>
                                    )}
                                </>
                            ) : (
                                <>
                                    Showing {filteredClients.length} of {trainer.clientRelations.length} clients
                                </>
                            )}
                        </Text>
                        <Button onClick={onClose}>
                            Close
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default ViewTrainerClientsModal;
